import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Search } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { adminApi, type AdminUserRow, type ListUsersInput } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useAutoFillPages } from "@/hooks/useAutoFillPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

export const Route = createFileRoute("/_authed/users")({
  component: UsersPage,
});

const STATUSES = ["", "active", "disabled"] as const;
const ROLES = ["", "platform_admin", "end_user"] as const;
type AgeBand = "" | "missing" | "under18" | "adult";
const PAGE_SIZE = 50;

function computeAge(dobMillis: number, now: Date = new Date()): number {
  const dob = new Date(dobMillis);
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [role, setRole] = useState<(typeof ROLES)[number]>("");
  const [schoolUrn, setSchoolUrn] = useState("");
  const [ageBand, setAgeBand] = useState<AgeBand>("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedSearch = useDebouncedValue(search);
  const debouncedSchoolUrn = useDebouncedValue(schoolUrn);

  const filters: ListUsersInput = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: status || undefined,
      role: role || undefined,
      schoolUrn: debouncedSchoolUrn.trim() || undefined,
      ageBand: ageBand || undefined,
      limit: PAGE_SIZE,
    }),
    [debouncedSearch, status, role, debouncedSchoolUrn, ageBand],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listUsers()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });
  // status / ageBand are filter pages on the server: a page can be empty
  // with more to come, and must not render as "no matching users".
  useAutoFillPages(query, (page) => page.users.length);

  const rows = query.data?.pages.flatMap((p) => p.users) ?? [];

  const columns = useMemo<ColumnDef<AdminUserRow, unknown>[]>(
    () => [
      {
        header: "Email",
        accessorKey: "email",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.email || "—"}</span>
            <span className="text-xs text-muted-foreground">
              {[row.original.firstName, row.original.lastName].filter(Boolean).join(" ") || "—"}
            </span>
          </div>
        ),
      },
      {
        header: "UID",
        accessorKey: "uid",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.uid}</span>
        ),
      },
      {
        header: "Role",
        accessorKey: "role",
        cell: ({ row }) =>
          row.original.role === "platform_admin" ? (
            <StatusPill tone="info">admin</StatusPill>
          ) : (
            <span className="text-xs text-muted-foreground">end user</span>
          ),
      },
      {
        header: "Status",
        accessorKey: "disabled",
        cell: ({ row }) =>
          row.original.disabled ? (
            <StatusPill tone="danger">disabled</StatusPill>
          ) : (
            <StatusPill tone="success">active</StatusPill>
          ),
      },
      {
        header: "School",
        accessorKey: "schoolName",
        cell: ({ row }) =>
          row.original.schoolName ? (
            <div className="flex flex-col">
              <span className="text-xs">{row.original.schoolName}</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {row.original.schoolUrn ?? ""}
              </span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Tier",
        accessorKey: "subscriptionTier",
        cell: ({ row }) => (
          <span className="text-xs">{row.original.subscriptionTier ?? "free"}</span>
        ),
      },
      {
        header: "Items",
        accessorKey: "itemsCreated",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.itemsCreated.toLocaleString()}</span>
        ),
      },
      {
        header: "Joined",
        accessorKey: "createdAt",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.createdAt
              ? format(new Date(row.original.createdAt), "yyyy-MM-dd")
              : "—"}
          </span>
        ),
      },
      {
        header: "DOB (set at first purchase)",
        accessorKey: "dateOfBirth",
        cell: ({ row }) => {
          const dob = row.original.dateOfBirth;
          if (!dob) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          const age = computeAge(dob);
          const under18 = age < 18;
          return (
            <div className="flex flex-col">
              <span className="font-mono text-xs">
                {format(new Date(dob), "yyyy-MM-dd")}
              </span>
              {under18 ? (
                <StatusPill tone="neutral">{age} yrs (under 18)</StatusPill>
              ) : (
                <span className="text-[10px] text-muted-foreground">{age} yrs</span>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  function exportCsv() {
    downloadCsv(
      `users-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      rows.map((r) => ({
        uid: r.uid,
        email: r.email,
        firstName: r.firstName ?? "",
        lastName: r.lastName ?? "",
        phoneNumber: r.phoneNumber ?? "",
        authSource: r.authSource ?? "",
        role: r.role ?? "end_user",
        disabled: r.disabled ? "true" : "false",
        schoolName: r.schoolName ?? "",
        schoolUrn: r.schoolUrn ?? "",
        subscriptionTier: r.subscriptionTier ?? "free",
        itemsCreated: r.itemsCreated,
        createdAt: r.createdAt ? new Date(r.createdAt).toISOString() : "",
        dateOfBirth: r.dateOfBirth ? new Date(r.dateOfBirth).toISOString().slice(0, 10) : "",
        age: r.dateOfBirth ? computeAge(r.dateOfBirth) : "",
      })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length.toLocaleString()} loaded
            {query.hasNextPage ? " — more available" : ""}.
          </p>
          {ageBand ? (
            <p className="text-xs text-muted-foreground">
              Filtered server-side, but this is a page count, not a platform total.
            </p>
          ) : null}
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">
              Search (email prefix or UID)
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                placeholder="ada@ or Zj1iTUq9DgNkLT…"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Status</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Role</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as (typeof ROLES)[number])}
            >
              {ROLES.map((r) => (
                <option key={r || "all"} value={r}>
                  {r || "All roles"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1 md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">School URN (exact)</label>
            <Input value={schoolUrn} onChange={(e) => setSchoolUrn(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Age band</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={ageBand}
              onChange={(e) => setAgeBand(e.target.value as AgeBand)}
            >
              <option value="">All</option>
              <option value="missing">DOB missing</option>
              <option value="under18">Under 18</option>
              <option value="adult">Adult</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={Boolean(query.hasNextPage)}
        onLoadMore={() => void query.fetchNextPage()}
        onRowClick={(r) => navigate({ to: "/users/$uid", params: { uid: r.uid } })}
        rowKey={(r) => r.uid}
        {...queryTableProps(query)}
      />
    </div>
  );
}
